import { Component, Output, EventEmitter, OnInit, Input, ViewChild } from '@angular/core';
import { PresetDateRanges, PresetDateRange, DateRange, PresetDateRangeCategory } from './date-range';
import { SessionStateService } from '../../core/user/session-state.service';
import { MyDatePipe } from './date.pipe';
import { DateRangeComponent } from './date-range-selector.component';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';


/**
 * U/I to choose a PresetDateRange. Automatically retains in session storage.
 */
@Component({
    selector: 'my-preset-date-range-selector',
    templateUrl: 'preset-date-range-selector.component.html'
})
export class PresetDateRangeComponent implements OnInit {

    /** Include manual range input? */
    @Input()
    includeManual: boolean = false;

    @Input()
    category: PresetDateRangeCategory = 'calendar';

    @Input()
    dropdownAlignment: "left" | "right" = "left";

    /** Event back to the caller when the preset changes */
    @Output()
    selectedChange = new EventEmitter<PresetDateRange>();

    @ViewChild(DateRangeComponent, {static: false}) customRangeComponent: DateRangeComponent;
    @ViewChild('myDropdown', {static: true}) dropdownRef: BsDropdownDirective;

    /** Track current selection */
    selected: PresetDateRange;


    constructor(private sessionState: SessionStateService) {
    }

    /**
     * Upon initialization, restore previous selection.
     */
    ngOnInit() {
        // Emit upon initialization to set the current value in the parent.
        let custom = this.sessionState.getCustomDateRange(this.category);
        if (this.includeManual && custom && custom.isDefined()) {
            this.selectManualRange();
        }
        else {
            this.selected = this.sessionState.getPresetDateRange(this.category);
            this.selectedChange.emit(this.selected);
        }
    };

    /** Get the list of predefined ranges to display as options */
    get presetDateRanges() {
        return PresetDateRanges.filter(preset => preset.category === this.category);
    }

    /** Determines if the dropdown menu should right-align with the selection box */
    get useDropdownMenuRight() {
      return this.dropdownAlignment === "right";
    }

    /**
     * User opening/closing the dropdown menu.
     * @param isOpenning
     */
    onShown() {
        if (this.includeManual) {
            // Refresh custom range component
            if (this.customRangeComponent) {
                this.customRangeComponent.refresh();
            }
        }
    }

    //For some reason dropdown wants to close on calendar click
    cancelAutoClose(e: MouseEvent) {
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
    }

    /**
     * User selected a preset.
     * May also be called by a parent component.
     */
    public selectThis(preset: PresetDateRange) {
        if (this.selected.index !== preset.index) {
            this.selected = preset;
            this.sessionState.setPresetDateRange(this.category, this.selected);
            this.selectedChange.emit(this.selected);
            if (this.includeManual) {
                this.dropdownRef.hide();
            }
        }
    }

    /**
     * User selected the manual date-range option.
     */
    selectManualRange() {
        const datePipe = new MyDatePipe();

        // DateRangeComponent will have already set it in SessionState.
        let selectedRange = this.sessionState.getCustomDateRange(this.category);
        if (selectedRange) {
            let displayStr =
                datePipe.transform(selectedRange.startDate) + " to " +
                datePipe.transform(selectedRange.endDate) + " ";

            this.selected = {
                index: -1,
                display: displayStr,
                getRange: (): DateRange => selectedRange
            } as PresetDateRange;

            this.selectedChange.emit(this.selected);
            this.dropdownRef.hide();
        }
        //else No custom date range - so we're still using the current preset.
    }
}
